import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "../../Assets/logo company.webp";
import { navTabs, SubTab, Tab } from "./tabs-data/tabs-data";

function Navbar() {
  const [services, setServices] = useState(0);
  const [hoveredTab, setHoveredTab] = useState<number | null>(null);
  const [menuView, setMenuView] = useState<boolean>(false);
  const [mobileSubmenu, setMobileSubmenu] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const handleTabClick = (tab: Tab | SubTab, index?: number) => {
    if (tab.path) {
      navigate(tab.path);
      setMenuView(false);
      setHoveredTab(null);
    } else if ("product_id" in tab && tab.product_id) {
      navigate(`/product/${tab.name}/${tab.product_id}`);
      setMenuView(false);
      setHoveredTab(null);
    } else if ("subTabs" in tab && index !== undefined) {
      setHoveredTab(hoveredTab === index ? null : index);
      setServices(0);
    }
  };

  const transition = {
    type: "spring",
    stiffness: 150,
    damping: 12,
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest(".nav-tabs-container")
      ) {
        setHoveredTab(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav className="fixed top-2 left-4 right-4 z-50">
      <div className="mx-auto px-4">
        <div
          className={`flex items-center justify-between p-3 rounded-2xl shadow-md border transition-all duration-300 bg-white`}
        >
          <img
            src={logo || "/placeholder.svg"}
            alt="Company Logo"
            className="w-32 h-16 cursor-pointer object-contain"
            onClick={() => navigate("/")}
            loading="lazy"
          />

          <div className="hidden md:flex items-center gap-8 relative nav-tabs-container">
            {navTabs.map((tab, index) => (
              <div key={tab.id} className="">
                <div
                  className={`relative cursor-pointer font-medium transition-colors duration-300 text-primary hover:text-primary/80`}
                >
                  <div
                    onClick={() => handleTabClick(tab, index)}
                    className="flex items-center"
                  >
                    {tab.name}
                    {tab.subTabs && <ChevronDown size={16} className="ml-1" />}
                  </div>
                  {hoveredTab === index && (
                    <motion.div
                      layoutId="underline"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            className={`md:hidden p-2 rounded-full text-primary`}
            onClick={() => setMenuView(true)}
          >
            <Menu size={24} />
          </button>

          <AnimatePresence mode="wait">
            {hoveredTab !== null && navTabs[hoveredTab]?.subTabs && (
              <motion.div
                key={hoveredTab}
                ref={dropdownRef}
                initial={{ opacity: 0, y: 10, height: 0 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  height: "auto",
                  transition: {
                    height: { duration: 0.3 },
                    opacity: { duration: 0.2, delay: 0.1 },
                  },
                }}
                exit={{
                  opacity: 0,
                  y: 10,
                  height: 0,
                  transition: {
                    height: { duration: 0.2, delay: 0.1 },
                    opacity: { duration: 0.1 },
                  },
                }}
                className="absolute top-full left-0 right-0 z-40 hidden md:block overflow-hidden bg-white rounded-2xl border mx-auto px-4 mt-2"
              >
                <div className="container mx-auto px-4 py-8 relative z-10">
                  {navTabs[hoveredTab]?.subTabs?.some((tab) => tab.brief) ? (
                    <div className="flex flex-col md:flex-row w-full bg-opacity-80 backdrop-blur-lg overflow-hidden rounded-2xl">
                      <div className="flex flex-col justify-start items-start gap-3 border-r border-gray-100 p-6 w-full md:w-1/3 bg-white/40 backdrop-blur-md">
                        {navTabs[hoveredTab]?.subTabs?.map((sub, index) => (
                          <motion.button
                            key={sub.id}
                            onClick={() => handleTabClick(sub)}
                            onMouseEnter={() => setServices(index)}
                            className={`relative text-md font-medium text-gray-700 hover:text-primary transition duration-300 px-5 py-3.5 w-full text-left rounded-xl shadow-sm hover:shadow-md bg-white/80 backdrop-blur-lg border overflow-hidden ${
                              services === index
                                ? "border-primary/50"
                                : "border-gray-100/70"
                            }`}
                            whileHover={{
                              scale: 1.02,
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                            }}
                            whileTap={{ scale: 0.98 }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 20,
                            }}
                          >
                            <span className="relative z-10">{sub.name}</span>
                            {services === index && (
                              <motion.div
                                className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-lg"
                                layoutId="serviceIndicator"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                              />
                            )}
                          </motion.button>
                        ))}
                      </div>

                      <motion.div
                        key={services}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="flex flex-col justify-center items-start p-8 w-full md:w-2/3"
                      >
                        {navTabs[hoveredTab]?.subTabs?.[services]?.img && (
                          <div className="relative w-full md:h-80 h-44 rounded-xl overflow-hidden mb-4 border border-gray-100 shadow-sm">
                            <img
                              src={navTabs[hoveredTab]?.subTabs?.[services]?.img}
                              alt={navTabs[hoveredTab]?.subTabs?.[services]?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <h3 className="text-xl font-semibold text-primary mb-3">
                          {navTabs[hoveredTab]?.subTabs?.[services]?.name}
                        </h3>
                        {navTabs[hoveredTab]?.subTabs?.[services]?.brief && (
                          <p className="text-md text-gray-700 font-medium leading-relaxed">
                            {navTabs[hoveredTab]?.subTabs?.[services]?.brief}
                          </p>
                        )}
                      </motion.div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {navTabs[hoveredTab]?.subTabs?.map((sub) => (
                        <motion.button
                          key={sub.id}
                          onClick={() => handleTabClick(sub)}
                          whileHover={{
                            y: -1,
                            transition: { duration: 0.2, ease: "easeOut" },
                          }}
                          whileTap={{ scale: 0.98 }}
                          transition={{
                            duration: 0.2,
                            ease: [0.25, 0.46, 0.45, 0.94],
                          }}
                          className="group relative text-sm font-medium text-slate-700 hover:text-slate-900 transition-all duration-250 px-5 py-4 w-full text-left rounded-lg bg-white hover:bg-slate-50 border border-slate-200/70 hover:border-slate-300 shadow-sm hover:shadow-md hover:shadow-slate-200/60 focus:outline-none overflow-hidden"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{sub.name}</span>
                            <ArrowRight
                              size={16}
                              className="text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all duration-200"
                              strokeWidth={1.5}
                            />
                          </div>

                          <div className="absolute bottom-0 left-0 h-0.5 w-0 group-hover:w-full bg-primary transition-all duration-300 ease-out" />
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {menuView && (
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex md:hidden fixed top-0 right-0 w-full h-full bg-white shadow-lg p-6 z-50"
            >
              <div className="absolute top-0 left-0 w-full h-full bg-primary/5 backdrop-blur-md -z-10" />

              <button
                className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 text-primary"
                onClick={() => setMenuView(false)}
              >
                <X size={24} />
              </button>

              <div className="mt-16 flex flex-col gap-5 w-full">
                {navTabs.map((tab) => (
                  <div key={tab.id} className="border-b border-gray-100 pb-3">
                    <div
                      onClick={() => {
                        if (!tab.subTabs) handleTabClick(tab);
                        setMobileSubmenu(
                          tab.subTabs
                            ? mobileSubmenu === tab.id
                              ? null
                              : tab.id
                            : null
                        );
                      }}
                      className="text-lg font-medium text-primary cursor-pointer flex justify-between items-center py-2"
                    >
                      {tab.name}
                      {tab.subTabs && (
                        <motion.div
                          animate={{
                            rotate: mobileSubmenu === tab.id ? 180 : 0,
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown size={18} className="ml-2" />
                        </motion.div>
                      )}
                    </div>

                    {tab.subTabs && mobileSubmenu === tab.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={transition}
                        className="pl-4 mt-2"
                      >
                        {tab.subTabs.map((sub) => (
                          <motion.div
                            key={sub.id}
                            onClick={() => handleTabClick(sub)}
                            className="py-2.5 text-gray-700 hover:text-primary cursor-pointer"
                            whileHover={{ x: 4 }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 30,
                            }}
                          >
                            {sub.name}
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}

export default Navbar;
